import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ArrowLeft, Heart, AlertTriangle, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const JeuResponsable = () => {
  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />
      <main className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link
            to="/guides"
            data-discover="true"
            className="inline-flex items-center text-toxic-green hover:text-opacity-80 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux guides
          </Link>

          <article className="bg-dark-card border-2 border-gray-800 rounded-2xl p-8 md:p-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Jeu Responsable
            </h1>

            <div className="prose prose-invert max-w-none">
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Parier en ligne peut être un loisir passionnant et divertissant, mais comme toute activité impliquant de l'argent, cela peut aussi devenir problématique si ce n'est pas pratiqué de manière responsable. Ce guide n'est pas là pour vous décourager, mais pour vous aider à maintenir une relation saine et équilibrée avec les paris en ligne, pour que cette activité reste un plaisir et ne devienne jamais une source de problèmes.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Comprendre les Risques
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Les paris en ligne sont conçus pour que la maison ait toujours un avantage mathématique. Sur le long terme, la plupart des parieurs perdent de l'argent. C'est la réalité, et l'accepter est essentiel. Si vous pariez, vous devez être prêt à perdre. Ne jamais parier avec de l'argent dont vous avez besoin pour vivre, pour payer vos factures, ou pour épargner.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Les gains sont possibles, et certains parieurs réussissent à être rentables sur le long terme. Mais cela nécessite une connaissance approfondie, une stratégie solide, une gestion de bankroll rigoureuse, et beaucoup de discipline. Pour la majorité des parieurs, les paris sont un loisir qui coûte de l'argent, comme aller au cinéma ou au restaurant. Acceptez cela avant de commencer.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Les paris ne sont pas un moyen de résoudre des problèmes financiers. Si vous avez des dettes, des difficultés financières, ou si vous cherchez à "rattraper" des pertes, les paris ne sont pas la solution. Ils ne feront qu'aggraver la situation. Cherchez de l'aide professionnelle pour vos problèmes financiers, pas dans les paris.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Fixer des Limites
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Toutes les plateformes agréées offrent des outils pour fixer des limites : limites de dépôt quotidiennes, hebdomadaires ou mensuelles, limites de mise, limites de temps. Utilisez ces outils. Fixez des limites réalistes basées sur votre budget, et respectez-les. Une fois qu'une limite est fixée, elle ne peut généralement être augmentée qu'après un délai de réflexion (24 à 48 heures), ce qui vous protège contre les décisions impulsives.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Ne fixez pas des limites trop élevées "au cas où". Fixez des limites qui correspondent à ce que vous pouvez vraiment vous permettre de perdre. Si vous gagnez et que vous voulez augmenter vos limites, attendez le délai de réflexion. Cela vous donne le temps de réfléchir à froid, pas dans l'émotion d'un gain.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Fixez aussi des limites de temps. Les paris peuvent être chronophages, surtout le live betting. Limitez le temps que vous passez à parier chaque jour ou chaque semaine. Ayez une vie en dehors des paris : travail, famille, hobbies, amis. Les paris ne doivent pas devenir votre seule activité.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Reconnaître les Signes d'Alerte
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Certains comportements indiquent que votre relation avec les paris devient problématique. Si vous pariez plus que vous ne pouvez vous le permettre, si vous mentez à votre entourage sur vos paris, si vous pariez pour "rattraper" des pertes, si les paris affectent votre travail ou vos relations, si vous vous sentez anxieux ou déprimé à cause des paris, il est temps de prendre du recul.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                D'autres signes incluent : penser constamment aux paris, avoir besoin de parier des montants de plus en plus importants pour ressentir la même excitation, avoir des difficultés à arrêter de parier, négliger d'autres activités importantes, emprunter de l'argent pour parier, ou se sentir coupable ou honteux à cause des paris.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Si vous reconnaissez ces signes, agissez immédiatement. Utilisez les outils d'auto-exclusion des plateformes, contactez une association d'aide, parlez à un professionnel. Plus tôt vous agissez, plus facile c'est de retrouver le contrôle.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Les Outils d'Auto-Exclusion
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Toutes les plateformes agréées offrent des outils d'auto-exclusion. Vous pouvez vous exclure temporairement (24 heures, 7 jours, 30 jours) ou définitivement. Pendant la période d'exclusion, vous ne pouvez pas accéder à votre compte, déposer, ou parier. C'est un outil puissant si vous sentez que vous perdez le contrôle.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                L'Autorité Nationale des Jeux offre aussi un système d'interdiction volontaire qui s'applique à toutes les plateformes agréées. Si vous vous inscrivez à ce système, vous ne pourrez pas créer de compte ou parier sur aucune plateforme agréée en France pendant au moins 3 ans. C'est une mesure radicale, mais nécessaire si vous avez un problème sérieux.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Utiliser ces outils n'est pas un signe de faiblesse, c'est un signe de force et de responsabilité. Si vous sentez que vous avez besoin d'une pause, prenez-la. Mieux vaut s'exclure temporairement que de continuer et d'aggraver la situation.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Maintenir une Perspective Saine
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Les paris doivent rester un loisir, pas une nécessité ou une obsession. Si vous ne pouvez pas imaginer arrêter de parier, si vous avez besoin de parier pour vous sentir bien, si les paris sont devenus le centre de votre vie, vous avez perdu la perspective. Prenez du recul, parlez à quelqu'un, utilisez les outils d'aide.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Rappelez-vous pourquoi vous avez commencé à parier : pour le divertissement, pour l'excitation, pour tester vos connaissances sportives. Si ces raisons ne sont plus valables, si vous pariez par habitude, par compulsion, ou par désespoir, arrêtez-vous. Les paris ne doivent jamais être une fuite ou un refuge.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Où Trouver de l'Aide
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Si vous avez besoin d'aide, plusieurs ressources sont disponibles. Joueurs Info Service (09 74 75 13 13) est un service d'écoute et d'information gratuit et anonyme. SOS Joueurs offre un accompagnement et un soutien aux joueurs en difficulté et à leurs proches. L'ANJ propose aussi des ressources et des informations sur le jeu responsable.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                N'hésitez pas à demander de l'aide. Les problèmes de jeu peuvent être traités, et plus tôt vous agissez, mieux c'est. Vous n'êtes pas seul, et il n'y a pas de honte à avoir besoin d'aide.
              </p>

              <div className="bg-toxic-green/10 border border-toxic-green/30 rounded-xl p-6 mt-12">
                <h3 className="text-2xl font-bold mb-4 text-white flex items-center">
                  <Heart className="w-6 h-6 text-toxic-green mr-3" />
                  Principes du Jeu Responsable
                </h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Ne jamais parier avec de l'argent dont vous avez besoin</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Fixez des limites de dépôt, de mise et de temps, et respectez-les</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Reconnaissez les signes d'alerte et agissez immédiatement</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Utilisez les outils d'auto-exclusion si nécessaire</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>N'hésitez pas à demander de l'aide si vous en avez besoin</span>
                  </li>
                </ul>
              </div>
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default JeuResponsable;

